import {
  Alert,
  AlertColor,
  Modal,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { desktopSx } from "src/utils/styles/modalStyles";
import PrescribeProvider from "src/context/PrescribeProvider";
import { useStepper } from "src/context/StepperProvider/useStepper";
import TotalDose from "./components/TotalDose";
import SelectMedicationsStep from "./components/SelectMedicationsStep";
import SelectDosagesStep from "./components/SelectDosagesStep";
import ReviewStep from "./components/ReviewStep";
import { useCallback, useMemo } from "react";
import { usePrescription } from "src/context/PrescriptionProvider";
import { useGetPatientVisitQuery } from "src/api/services/onlineVisitsApi";
import { skipToken } from "@reduxjs/toolkit/query";

const steps = ["Select medications", "Select dosage", "Review"];

interface Props {
  open: boolean;
  onClose: () => void;
  patientRegion: string | null;
  onSuccess: () => Promise<void>;
}
const PrescribeModal = ({ open, onClose, patientRegion, onSuccess }: Props) => {
  const { activeStep } = useStepper();
  const productSlug = usePrescription((p) => p.product.slug);
  const visitId = usePrescription((p) => p.online_visit_id);
  const { data: visit } = useGetPatientVisitQuery(
    visitId ? visitId : skipToken,
  );

  const flag = useMemo(() => {
    if (visit == null) return null;

    const flagColor = visit.metadata?.flag;

    if (flagColor == null) return null;

    return flagColor as AlertColor;
  }, [visit]);

  const twoStepProducts = useMemo(
    () =>
      ["bc", "ed-ot", "amh-ot", "b-ot", "bb-ot", "sl-ot", "os-ot"].includes(
        productSlug,
      ),
    [productSlug],
  );

  const handleClose = useCallback(async () => {
    onSuccess().then(onClose);
  }, [onClose, onSuccess]);

  const modalSteps = useMemo(
    () => (twoStepProducts ? steps.filter((_, idx) => idx !== 1) : steps),
    [twoStepProducts],
  );

  const showDosageStep = useMemo(
    () => activeStep === 1 && !twoStepProducts,
    [activeStep, twoStepProducts],
  );

  return (
    <PrescribeProvider region={patientRegion}>
      <Modal open={open} onClose={onClose}>
        <Stack gap={3} sx={{ ...desktopSx, minWidth: 700, minHeight: 700 }}>
          {/* header */}
          <Stepper activeStep={activeStep} alternativeLabel>
            {modalSteps.map((step) => (
              <Step key={step}>
                <StepLabel
                  slotProps={{
                    stepIcon: {
                      fontSize: "large",
                    },
                  }}
                >
                  <Typography>{step}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {flag ? (
            <Alert severity={flag}>
              <Typography>
                {flag === "success"
                  ? "Based on their intake this patient is eligible for GLP-1 medications"
                  : "Patient indicated a history of pancreatitis, please review"}
              </Typography>
            </Alert>
          ) : null}

          <TotalDose />

          {/* body */}
          {activeStep === 0 ? (
            <SelectMedicationsStep />
          ) : showDosageStep ? (
            <SelectDosagesStep />
          ) : (
            <ReviewStep onSuccess={handleClose} />
          )}
        </Stack>
      </Modal>
    </PrescribeProvider>
  );
};

export default PrescribeModal;
